import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '@/lib/api-client';
import { useI18n } from '@/lib/i18n';
import { getCached } from '@/lib/reference-data-cache';
import { Badge, Button, Card, CardContent, DropdownSelect, EmptyState, ErrorState, Input, PageHeader, SkeletonRow, SkeletonText, Textarea, useConfirm } from '@/components/ui';

/**
 * Admin > Groups: list and full-page editor.
 *
 * GROUP-03 (FRESHDESK_PARITY_GAP_PLAN.md §7): the old Groups admin surface
 * (AdminSettingsPage.tsx, now deleted) exposed name/description/autoAssign and
 * member add/remove, but never exposed `businessHoursId` or `escalateTo` even
 * though both are fully supported end-to-end by the backend (CreateGroupDto/
 * UpdateGroupDto, GroupsService, Prisma Group model). This page adds those two
 * fields and moves Groups onto the same dedicated list+editor pattern already
 * used for Roles (RolesAdminPages.tsx) and Agents (AgentsAdminPages.tsx).
 *
 * GROUP-01 (round-robin/load-based auto-assignment) is explicitly out of scope
 * per product decision - ticket assignment stays manual. The `autoAssign`
 * field kept here is the existing boolean that already existed before this
 * change; it is surfaced as-is, not extended with any new routing logic.
 */

export type BusinessHours = { id: string; name: string; timezone?: string };

export type Agent = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  isActive: boolean;
};

export type GroupMember = {
  id?: string;
  userId: string;
  user: { id: string; firstName: string; lastName: string; email: string; avatar?: string | null };
};

export type Group = {
  id: string;
  name: string;
  description?: string | null;
  businessHoursId?: string | null;
  escalateTo?: string | null;
  autoAssign: boolean;
  members?: GroupMember[];
  businessHours?: BusinessHours | null;
  createdAt?: string;
};

const problem = (error: unknown, fallback: string) =>
  error instanceof ApiError ? error.message : fallback;

function ErrorBanner({ message, onRetry }: { message: string; onRetry?: () => void }) {
  if (!message) return null;
  return <ErrorState message={message} onRetry={onRetry} />;
}

function AdminHeader({
  breadcrumb,
  title,
  subtitle,
  actions,
}: {
  breadcrumb: string;
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}) {
  const { t } = useI18n();
  return (
    <PageHeader
      className="mb-0"
      breadcrumb={[{ label: t('nav.settings'), to: '/settings' }, { label: breadcrumb }]}
      title={title}
      description={subtitle}
      actions={actions}
    />
  );
}

function HelpPanel({ children }: { children: React.ReactNode }) {
  return (
    <aside className="space-y-4 text-sm leading-relaxed text-ink-muted 2xl:border-l 2xl:border-line 2xl:pl-6">
      {children}
    </aside>
  );
}

function agentName(agent: Agent | null | undefined, unassigned: string): string {
  if (!agent) return unassigned;
  return `${agent.firstName} ${agent.lastName}`.trim() || agent.email;
}

function Pagination({
  page,
  totalPages,
  onChange,
}: {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
}) {
  const { t } = useI18n();
  if (totalPages <= 1) return null;
  const pages: Array<number | 'ellipsis'> = [];
  for (let i = 1; i <= totalPages; i += 1) {
    if (i === 1 || i === totalPages || Math.abs(i - page) <= 1) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== 'ellipsis') {
      pages.push('ellipsis');
    }
  }
  return (
    <nav aria-label={t('adm.pagination')} className="flex flex-wrap items-center justify-center gap-1 border-t border-line px-5 py-3">
      <button
        type="button"
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
        aria-label={t('adm.previousPage')}
        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-line-strong bg-surface text-ink shadow-card transition-all duration-150 hover:border-brand-300 hover:bg-brand-50 disabled:cursor-not-allowed disabled:opacity-40"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {pages.map((entry, index) =>
        entry === 'ellipsis' ? (
          <span key={`ellipsis-${index}`} className="px-1.5 text-sm text-ink-subtle">
            …
          </span>
        ) : (
          <button
            key={entry}
            type="button"
            aria-current={entry === page}
            onClick={() => onChange(entry)}
            className={`inline-flex h-8 min-w-8 items-center justify-center rounded-lg px-2 text-sm font-medium transition-colors duration-150 ${
              entry === page
                ? 'bg-brand-600 text-white shadow-card'
                : 'border border-line-strong bg-surface text-ink hover:border-brand-300 hover:bg-brand-50'
            }`}
          >
            {entry}
          </button>
        ),
      )}
      <button
        type="button"
        disabled={page >= totalPages}
        onClick={() => onChange(page + 1)}
        aria-label={t('adm.nextPage')}
        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-line-strong bg-surface text-ink shadow-card transition-all duration-150 hover:border-brand-300 hover:bg-brand-50 disabled:cursor-not-allowed disabled:opacity-40"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </nav>
  );
}

const GROUPS_PAGE_SIZE = 30;

export function GroupsListPage() {
  const { t } = useI18n();
  const { confirm } = useConfirm();
  const [groups, setGroups] = React.useState<Group[]>([]);
  const [agents, setAgents] = React.useState<Agent[]>([]);
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [busyId, setBusyId] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      setError('');
      const [nextGroups, nextAgents] = await Promise.all([
        api.get<Group[]>('/admin/groups'),
        getCached<Agent[]>('/admin/agents'),
      ]);
      setGroups(nextGroups);
      setAgents(nextAgents);
    } catch (cause) {
      setError(problem(cause, t('common.requestFailed')));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  const agentsById = React.useMemo(() => new Map(agents.map((agent) => [agent.id, agent])), [agents]);

  const filtered = search.trim()
    ? groups.filter((group) => group.name.toLowerCase().includes(search.trim().toLowerCase()))
    : groups;

  const totalPages = Math.max(1, Math.ceil(filtered.length / GROUPS_PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const visible = filtered.slice((safePage - 1) * GROUPS_PAGE_SIZE, safePage * GROUPS_PAGE_SIZE);
  const rangeStart = filtered.length === 0 ? 0 : (safePage - 1) * GROUPS_PAGE_SIZE + 1;
  const rangeEnd = Math.min(safePage * GROUPS_PAGE_SIZE, filtered.length);

  function updateSearch(value: string) {
    setSearch(value);
    setPage(1);
  }

  async function remove(group: Group) {
    if (!await confirm({ title: t('adm.groups.confirmTitle', { name: group.name }), description: t('adm.groups.confirmDesc'), confirmLabel: t('adm.groups.confirmLabel') })) {
      return;
    }
    setBusyId(group.id);
    try {
      await api.delete(`/admin/groups/${group.id}`);
      await load();
    } catch (cause) {
      setError(problem(cause, t('common.requestFailed')));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="animate-fade-in space-y-6">
      <AdminHeader
        breadcrumb={t('adm.groups.title')}
        title={t('adm.groups.title')}
        subtitle={t('adm.groups.subtitle')}
        actions={
          <Link
            to="/settings/groups/new"
            className="inline-flex h-10 items-center justify-center rounded-lg bg-brand-600 px-4 text-sm font-medium text-white shadow-card transition-all duration-150 hover:bg-brand-700 hover:shadow-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
          >
            {t('adm.groups.newGroup')}
          </Link>
        }
      />
      <ErrorBanner message={error} onRetry={() => void load()} />

      <div className="grid gap-6 2xl:grid-cols-[minmax(0,1fr)_20rem]">
        <Card className="overflow-hidden">
          <div className="grid gap-3 border-b border-line px-5 py-3 sm:grid-cols-2 sm:items-center">
            <span className="text-sm font-medium text-ink-muted">
              {filtered.length === 0 ? t('adm.groups.zero') : t('adm.groups.showing', { start: rangeStart, end: rangeEnd, total: filtered.length })}
            </span>
            <div className="w-full sm:w-56 sm:justify-self-end">
              <Input
                placeholder={t('adm.groups.searchPlaceholder')}
                aria-label={t('adm.groups.searchAria')}
                value={search}
                onChange={(event) => updateSearch(event.target.value)}
                className="h-9 w-full"
              />
            </div>
          </div>

          <CardContent className="divide-y divide-line p-0">
            {loading && <SkeletonRow rows={5} />}
            {!loading && visible.length === 0 && (
              <EmptyState
                title={search.trim() ? t('adm.groups.noMatch') : t('adm.groups.createFirst')}
                description={search.trim()
                  ? t('adm.groups.tryDifferent')
                  : t('adm.groups.useNew')}
              />
            )}
            {visible.length > 0 && (
              <div
                className="hidden items-center gap-4 px-5 py-2 text-xs font-medium uppercase tracking-wide text-ink-subtle xl:grid xl:grid-cols-12"
              >
                <span className="xl:col-span-3">{t('adm.groups.colGroup')}</span>
                <span className="xl:col-span-2">{t('adm.groups.activeAgents')}</span>
                <span className="xl:col-span-2">{t('adm.groups.businessHours')}</span>
                <span className="xl:col-span-1">{t('adm.groups.routing')}</span>
                <span className="xl:col-span-2">{t('adm.groups.escalatesTo')}</span>
                <span className="text-right xl:col-span-2">{t('common.actions')}</span>
              </div>
            )}
            {visible.map((group) => (
              <div
                key={group.id}
                className="group relative grid items-center gap-4 p-5 transition-colors duration-150 hover:bg-brand-50/50 xl:grid-cols-12"
              >
                <div className="min-w-0 xl:col-span-3">
                  <Link
                    to={`/settings/groups/${group.id}`}
                    className="block max-w-full truncate font-semibold text-brand-700 transition-colors hover:text-brand-800 hover:underline"
                  >
                    {group.name}
                  </Link>
                  {group.description && (
                    <p className="truncate text-sm text-ink-muted">{group.description}</p>
                  )}
                </div>

                <span
                  className="min-w-0 truncate whitespace-nowrap text-sm text-ink-muted xl:col-span-2"
                  title={t('adm.groups.activeAgents')}
                >
                  {t((group.members?.length ?? 0) === 1 ? 'adm.groups.activeAgentOne' : 'adm.groups.activeAgentMany', { count: group.members?.length ?? 0 })}
                </span>
                <span className="min-w-0 truncate text-sm text-ink-muted xl:col-span-2" title={t('adm.groups.businessHours')}>
                  {group.businessHours?.name ?? t('adm.groups.defaultHours')}
                </span>
                <span className="min-w-0 truncate text-sm text-ink-muted xl:col-span-1" title={t('adm.groups.routingMethod')}>
                  {t('adm.groups.manual')}
                </span>
                <span className="min-w-0 truncate text-sm text-ink-muted xl:col-span-2" title={t('adm.groups.escalatesTo')}>
                  {t('adm.groups.escalatesToName', { name: agentName(group.escalateTo ? agentsById.get(group.escalateTo) : null, t('adm.groups.unassigned')) })}
                </span>

                <div className="flex shrink-0 items-center gap-2 opacity-80 transition-opacity duration-150 group-hover:opacity-100 xl:col-span-2 xl:justify-end">
                  <Link
                    to={`/settings/groups/${group.id}`}
                    aria-label={t('adm.groups.editAria', { name: group.name })}
                    title={t('adm.edit')}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-line-strong bg-surface text-ink shadow-card transition-all duration-150 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path
                        d="M3 21l3.6-1 12-12a1.5 1.5 0 0 0 0-2.1l-1.5-1.5a1.5 1.5 0 0 0-2.1 0l-12 12L3 21Z"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Link>
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={busyId === group.id}
                    onClick={() => void remove(group)}
                  >
                    {busyId === group.id ? t('adm.deleting') : t('common.delete')}
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
          <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />
        </Card>

        <HelpPanel>
          <h2 className="text-base font-semibold text-ink">{t('adm.groups.manualTitle')}</h2>
          <p>{t('adm.groups.manualBody')}</p>
          <h2 className="text-base font-semibold text-ink">{t('adm.groups.escalationTitle')}</h2>
          <p>{t('adm.groups.escalationBody')}</p>
        </HelpPanel>
      </div>
    </div>
  );
}

export function GroupEditPage() {
  const { t } = useI18n();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [group, setGroup] = React.useState<Group | null>(null);
  const [agents, setAgents] = React.useState<Agent[]>([]);
  const [businessHours, setBusinessHours] = React.useState<BusinessHours[]>([]);
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [autoAssign, setAutoAssign] = React.useState(true);
  const [businessHoursId, setBusinessHoursId] = React.useState('');
  const [escalateTo, setEscalateTo] = React.useState('');
  const [memberId, setMemberId] = React.useState('');
  const [error, setError] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [saved, setSaved] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      setError('');
      const [allAgents, allBusinessHours] = await Promise.all([
        getCached<Agent[]>('/admin/agents'),
        getCached<BusinessHours[]>('/admin/business-hours'),
      ]);
      setAgents(allAgents);
      setBusinessHours(allBusinessHours);

      if (!isNew) {
        const existing = await api.get<Group>(`/admin/groups/${id}`);
        setGroup(existing);
        setName(existing.name);
        setDescription(existing.description ?? '');
        setAutoAssign(existing.autoAssign);
        setBusinessHoursId(existing.businessHoursId ?? '');
        setEscalateTo(existing.escalateTo ?? '');
      }
    } catch (cause) {
      setError(problem(cause, t('common.requestFailed')));
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const activeAgents = agents.filter((agent) => agent.isActive);
  const members = group?.members ?? [];
  const availableToAdd = activeAgents.filter(
    (agent) => !members.some((member) => member.userId === agent.id),
  );

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      setError('');
      setSaved(false);
      const payload = {
        name,
        description: description || undefined,
        autoAssign,
        businessHoursId: businessHoursId || null,
        escalateTo: escalateTo || null,
      };
      if (isNew) {
        const created = await api.post<Group>('/admin/groups', payload);
        navigate(`/settings/groups/${created.id}`, { replace: true });
        setSaved(true);
      } else {
        const updated = await api.put<Group>(`/admin/groups/${id}`, payload);
        setGroup(updated);
        setSaved(true);
      }
    } catch (cause) {
      setError(problem(cause, t('common.requestFailed')));
    } finally {
      setSaving(false);
    }
  }

  async function addMember(event: React.FormEvent) {
    event.preventDefault();
    if (!id || isNew || !memberId) return;
    setSaving(true);
    try {
      setError('');
      await api.post(`/admin/groups/${id}/members`, { userId: memberId });
      setMemberId('');
      const refreshed = await api.get<Group>(`/admin/groups/${id}`);
      setGroup(refreshed);
    } catch (cause) {
      setError(problem(cause, t('common.requestFailed')));
    } finally {
      setSaving(false);
    }
  }

  async function removeMember(userId: string) {
    if (!id || isNew) return;
    setSaving(true);
    try {
      setError('');
      await api.deleteWithBody(`/admin/groups/${id}/members`, { userId });
      const refreshed = await api.get<Group>(`/admin/groups/${id}`);
      setGroup(refreshed);
    } catch (cause) {
      setError(problem(cause, t('common.requestFailed')));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="animate-fade-in space-y-6" onSubmit={save}>
      <AdminHeader
        breadcrumb={t('adm.groups.title')}
        title={isNew ? t('adm.groups.newGroup') : t('adm.groups.editGroup')}
        actions={
          <>
            <Button type="button" variant="outline" onClick={() => navigate('/settings/groups')}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={saving || loading}>
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </>
        }
      />
      <ErrorBanner message={error} />
      {saved && (
        <div
          role="status"
          className="animate-fade-in rounded-xl border border-success-500/20 bg-success-50 px-4 py-3 text-sm text-success-700"
        >
          {t('adm.groups.saved')}
        </div>
      )}

      <div className="grid gap-6 2xl:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="space-y-6">
          <Card>
            <CardContent className="space-y-5 p-6">
              <div className="grid gap-2 md:grid-cols-[12rem_minmax(0,1fr)] md:items-center">
                <label htmlFor="group-name" className="text-sm font-semibold text-ink">
                  {t('common.name')} <span className="text-accent-500">*</span>
                </label>
                <Input id="group-name" required value={name} onChange={(event) => setName(event.target.value)} />
              </div>

              <div className="grid gap-2 md:grid-cols-[12rem_minmax(0,1fr)] md:items-start">
                <label htmlFor="group-description" className="pt-2 text-sm font-semibold text-ink">
                  {t('adm.description')}
                </label>
                <Textarea
                  id="group-description"
                  rows={2}
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                />
              </div>

              <div className="grid gap-2 md:grid-cols-[12rem_minmax(0,1fr)] md:items-start">
                <label htmlFor="group-business-hours" className="pt-2 text-sm font-semibold text-ink">
                  {t('adm.groups.businessHours')}
                </label>
                <div>
                  <DropdownSelect
                    options={[
                      { value: '', label: t('adm.groups.accountDefault') },
                      ...businessHours.map((hours) => ({ value: hours.id, label: hours.name })),
                    ]}
                    value={businessHoursId}
                    onChange={(value) => setBusinessHoursId(value)}
                  />
                  <p className="mt-1.5 text-xs text-ink-subtle">
                    {t('adm.groups.hoursHelp')}{' '}
                    <Link to="/settings/business-hours" className="text-brand-700 hover:underline">
                      {t('adm.groups.manageHours')}
                    </Link>
                  </p>
                </div>
              </div>

              <div className="grid gap-2 md:grid-cols-[12rem_minmax(0,1fr)] md:items-start">
                <label htmlFor="group-escalate-to" className="pt-2 text-sm font-semibold text-ink">
                  {t('adm.groups.escalateTo')}
                </label>
                <div>
                  <DropdownSelect
                    options={[
                      { value: '', label: t('adm.groups.noEscalation') },
                      ...activeAgents.map((agent) => ({ value: agent.id, label: agentName(agent, t('adm.groups.unassigned')) })),
                    ]}
                    value={escalateTo}
                    onChange={(value) => setEscalateTo(value)}
                  />
                  <p className="mt-1.5 text-xs text-ink-subtle">
                    {t('adm.groups.escalateHelp')}
                  </p>
                </div>
              </div>

              <div className="grid gap-2 md:grid-cols-[12rem_minmax(0,1fr)] md:items-center">
                <span className="text-sm font-semibold text-ink">{t('adm.groups.assignment')}</span>
                <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-ink">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-line-strong text-brand-600 transition focus:ring-2 focus:ring-brand-500"
                    checked={autoAssign}
                    onChange={(event) => setAutoAssign(event.target.checked)}
                  />
                  {t('adm.groups.autoAssign')}
                </label>
              </div>
              <p className="text-xs text-ink-subtle">{t('adm.groups.autoAssignNote')}</p>
            </CardContent>
          </Card>

          {!isNew && (
            <Card>
              <CardContent className="space-y-3 p-6">
                <div className="flex items-baseline justify-between gap-2 border-b border-line pb-3">
                  <h2 className="text-lg font-semibold text-ink">{t('adm.groups.members')}</h2>
                  <Link to="/settings/agents" className="text-sm font-medium text-brand-700 hover:underline">
                    {t('adm.groups.manageAgents')}
                  </Link>
                </div>

                {loading ? (
                  <SkeletonText lines={3} className="pt-2" />
                ) : (
                  <>
                    <div className="flex flex-wrap items-end gap-2 pt-2">
                      <DropdownSelect
                        options={[
                          { value: '', label: t('adm.groups.selectActiveAgent') },
                          ...availableToAdd.map((agent) => ({ value: agent.id, label: agentName(agent, t('adm.groups.unassigned')) })),
                        ]}
                        value={memberId}
                        onChange={(value) => setMemberId(value)}
                        className="max-w-xs"
                      />
                      <Button type="button" size="sm" disabled={!memberId || saving} onClick={addMember}>
                        {t('adm.groups.addMember')}
                      </Button>
                    </div>

                    {members.length ? (
                      <ul className="mt-3 space-y-2">
                        {members.map((member) => (
                          <li
                            key={member.userId}
                            className="flex min-w-0 flex-wrap items-center justify-between gap-3 rounded-lg border border-line bg-surface px-3 py-2 text-sm"
                          >
                            <span className="min-w-0 flex-1 truncate">
                              {member.user.firstName} {member.user.lastName}
                              <span className="ml-2 text-ink-subtle">{member.user.email}</span>
                            </span>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              disabled={saving}
                              onClick={() => void removeMember(member.userId)}
                            >
                              {t('adm.groups.remove')}
                            </Button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <EmptyState
                        title={t('adm.groups.noMembers')}
                        description={t('adm.groups.noMembersDesc')}
                      />
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        <HelpPanel>
          <h2 className="text-base font-semibold text-ink">{t('adm.groups.aboutTitle')}</h2>
          <p>{t('adm.groups.aboutBody')}</p>
          {!isNew && (
            <p>
              <Badge variant="outline">{t(members.length === 1 ? 'adm.groups.memberOne' : 'adm.groups.memberMany', { count: members.length })}</Badge>
            </p>
          )}
        </HelpPanel>
      </div>
    </form>
  );
}
